import clsx from 'clsx'
import { PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { SizedMixin } from '@/internal/mixin/sizedMixin'

import DapDSOptionItem from '../option-item/option-item.component'
import styles from './option-list.scss?inline'

/**
 * `dap-ds-option-list`
 * @summary An option list is a list of selectable options.
 * @element dap-ds-option-list
 * @title - Option list
 *
 * @event {{ void }} dds-cancel - Fired when the escape key is pressed.
 * @event {{ option: DapDSOptionItem, value: string, index: number, text: string }} dds-option-change - Fired when the option list value changes.
 * @event {{ void }} dds-keydown - Fired when a key is pressed down.
 * @event {{ elements: DapDSOptionItem[] }} dds-list-changed - Fired when the available options list changes.
 *
 * @csspart base - The main option list container.
 *
 * @slot - The default slot for the options.
 */
@customElement('dap-ds-option-list')
export default class DapDSOptionList extends SizedMixin(DdsElement) {
  /** The filter text of the option list */
  @property() filterText = ''

  /** The value of the option list */
  @property({ type: String, reflect: true }) value?: string

  /** The focused state of the option list. */
  @property({ type: Boolean }) focused = false

  /** Whether the option list is searchable */
  @property({ type: Boolean }) search = false

  /** The search mode of the option list. Default is `none` */
  @property() searchMode: 'none' | 'typehead' | 'autocomplete' | 'manual' =
    'none'

  /**
   * @ignore
   */
  @property({ type: String }) searchOnValueSet = 'true'

  /** Whether the option list items are selectable, whether it shows the selected icon */
  @property({ type: Boolean }) selectable = false

  /**
   * @ignore
   */
  @property({ type: Array })
  public options?: any[]

  /**
   * @ignore
   */
  @property({ type: Boolean }) disableDelaySet = false

  queryTimeout: number | undefined
  queryString = ''
  firstLoad = true

  @state()
  private currentOption: any

  @state()
  public selectedIndex: number

  @state()
  private optionEls: DapDSOptionItem[] = []

  @state()
  private availableOptions: DapDSOptionItem[] = []

  get isAutoComplete() {
    return this.searchMode === 'autocomplete' || this.searchMode === 'manual'
  }

  static readonly styles = unsafeCSS(styles)

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    this.setAttribute('role', 'listbox')
  }

  getAllOptions() {
    const mainSlot = this.shadowRoot?.querySelector('slot')
    const assignedElement = mainSlot?.assignedElements()[0]
    const embeddedSlot =
      assignedElement?.nodeName === 'SLOT'
        ? (
            mainSlot?.assignedElements()[0] as HTMLSlotElement
          ).assignedElements()
        : mainSlot?.assignedElements()

    if (!embeddedSlot) {
      return [...this.querySelectorAll<DapDSOptionItem>('dap-ds-option-item')]
    } else {
      return [...(embeddedSlot as DapDSOptionItem[])]
    }
  }

  private getAllRenderedOptions(): DapDSOptionItem[] {
    const ro =
      this.shadowRoot?.querySelectorAll<DapDSOptionItem>('dap-ds-option-item')
    return ro ? [...ro] : []
  }

  getAllAvailabeOptions() {
    return this.availableOptions
  }

  getActiveOption() {
    if (!this.currentOption) {
      return null
    }

    const activeOption = this.getAllRenderedOptions().find(
      el => el.value === this.currentOption.value,
    )

    return activeOption
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    if (
      _changedProperties.has('value') &&
      !_changedProperties.has('currentOption') &&
      this.searchOnValueSet === 'true'
    ) {
      const option = this.availableOptions.find(
        option => option.value === this.value,
      )

      if (option) {
        this.changeOption(option, _changedProperties.get('value'))

        this.selectedIndex = this.findIndexByValue(
          this.availableOptions,
          this.value,
        )

        this.emit('dds-option-change', {
          ...this.getEventDetails(this.currentOption),
        })
      }
    }

    if (
      _changedProperties.has('options') ||
      _changedProperties.has('optionEls')
    ) {
      this.availableOptions = this.options || this.optionEls
      this.emit('dds-list-changed', { elements: this.availableOptions })
    }
  }

  protected shouldUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): boolean {
    if (_changedProperties.has('filterText')) {
      if (
        this.filterText &&
        this.searchMode !== 'none' &&
        this.searchMode !== 'manual'
      ) {
        this.filterAvailableOptions()
        this.selectedIndex = 0
        this.setCurrentOption(this.availableOptions[0], false)
        this.emit('dds-list-changed', { elements: this.availableOptions })
      } else if (this.filterText && this.searchMode === 'manual') {
        if (this.selectedIndex === -1 && this.availableOptions.length > 0) {
          this.selectedIndex = 0
          this.setCurrentOption(this.availableOptions[0], false)
        }
      } else if (this.filterText && this.searchMode === 'none') {
        this.filterAvailableOptions()
        this.selectedIndex = -1
        this.setCurrentOption(null, false)
        this.setSelectedOption()
        this.emit('dds-list-changed', { elements: this.availableOptions })
      } else {
        this.availableOptions = this.options || this.optionEls
        this.selectedIndex = -1
        this.setCurrentOption(null, false)
        this.emit('dds-list-changed', { elements: this.availableOptions })
      }
    }

    return super.shouldUpdate(_changedProperties)
  }

  public filterAvailableOptions(): void {
    const valueLowerCase = this.filterText.toLowerCase()
    this.availableOptions = (this.options || this.optionEls).filter(item => {
      if (!item.disabled) {
        const itemTextLowerCase = item.optionText.toLowerCase()
        return itemTextLowerCase.startsWith(valueLowerCase)
      }
    })
  }

  attributeChangedCallback(name: string, oldValue: any, newValue: any) {
    super.attributeChangedCallback(name, oldValue, newValue)
    if (
      name === 'value' &&
      oldValue !== newValue &&
      this.searchOnValueSet === 'true'
    ) {
      if (newValue) {
        const option = this.availableOptions.find(el => el.value === newValue)

        this.setSelectedOption(option)
        this.selectionChanged(option)

        this.emit('dds-option-change', {
          ...this.getEventDetails(this.currentOption),
        })
      } else {
        this.setSelectedOption()
        this.selectionChanged()
      }
    }
  }

  public setOptionsFromSlottedItems(): void {
    const elements = this.getAllOptions()
    this.availableOptions = elements
    this.optionEls = elements
  }

  private handleKeydown(event: KeyboardEvent): void {
    event.preventDefault()

    this.handleKeyCodes(event.key, event.code)
  }

  private scrollToActiveOption(): void {
    const activeOption = this.getAllRenderedOptions().find(
      el => el.value === this.currentOption.value,
    )

    if (activeOption) {
      activeOption.scrollIntoView({ block: 'nearest' })
    }
  }

  private handleNavigationKey(key: string): void {
    if (key === 'ArrowDown') {
      this.selectedIndex = this.findNearestEnabled(
        this.availableOptions,
        this.selectedIndex,
        1,
      )
    } else if (key === 'ArrowUp') {
      const nearest = this.findNearestEnabled(
        this.availableOptions,
        this.selectedIndex,
        this.selectedIndex === -1 ? 1 : -1,
      )
      this.selectedIndex =
        nearest === this.availableOptions.length ? -1 : nearest
    } else if (key === 'Home') {
      this.selectedIndex = this.availableOptions[0]?.disabled
        ? this.findNearestEnabled(this.availableOptions, -1, 1)
        : 0
    } else if (key === 'End') {
      const nearest = this.findNearestEnabled(
        this.availableOptions,
        this.availableOptions.length,
        -1,
      )
      this.selectedIndex =
        nearest === this.availableOptions.length ? -1 : nearest
    }
  }

  private handleActionKey(key: string): void {
    if (key === 'Escape') {
      this.emit('dds-cancel')
    } else if (key === 'Enter') {
      this.setSelectedOption(this.availableOptions[this.selectedIndex])
      this.selectionChanged(this.availableOptions[this.selectedIndex])
      this.emit('dds-option-change', {
        ...this.getEventDetails(this.currentOption),
      })
    }
  }

  private handleTypeheadKey(key: string): void {
    if (
      this.searchMode === 'typehead' &&
      (key?.length === 1 || key === 'Backspace')
    ) {
      this.buildQueryString(key)
    }
  }

  private handleKeyCodes(key: string, keyCode?: string) {
    if (!this.focused) {
      return
    }

    this.handleNavigationKey(key)
    this.handleActionKey(key)
    this.handleTypeheadKey(key)

    if (this.selectedIndex >= 0) {
      const option = this.availableOptions[this.selectedIndex]
      this.setCurrentOption(option)
      this.emit('dds-keydown', {
        code: keyCode,
        key,
        ...this.getEventDetails(option),
      })
    }
  }

  private getEventDetails(option?: DapDSOptionItem) {
    if (!option) {
      return undefined
    }

    let opt = option

    if (option.tagName !== 'DAP-DS-OPTION-ITEM') {
      opt = option.closest('dap-ds-option-item')!
    }

    return {
      opt,
      value: opt?.value,
      text: opt?.optionText,
      index: this.selectedIndex,
    }
  }

  private findNearestEnabled(
    allOptions: DapDSOptionItem[],
    current: number,
    step: number,
  ) {
    let nextIndex: number = current
    let nextOption: DapDSOptionItem

    do {
      nextIndex += step
      nextOption = allOptions[nextIndex]
      if (nextOption === undefined) {
        break
      }
    } while (nextOption?.disabled)

    return nextOption ? nextIndex : current
  }

  private findIndexByValue(
    allOptions: DapDSOptionItem[],
    value?: string,
  ): number {
    return allOptions.findIndex(option => option.value === value)
  }

  private clearItemFocusStates() {
    this.availableOptions.forEach(el => {
      el.focused = false
      el.tabIndex = -1
    })
  }

  // Only set the current option without selecting it, eg: focus
  private setCurrentOption(option?: DapDSOptionItem | null, setFocus = true) {
    this.clearItemFocusStates()

    if (option) {
      this.currentOption = option
      option.focused = true
      option.tabIndex = 0

      this.emit('dds-current-change', {
        ...this.getEventDetails(option),
      })

      if (setFocus) {
        this.setFocusedOption(option)
      } else {
        this.scrollToActiveOption()
      }
    } else {
      this.currentOption = null

      this.emit('dds-current-change', null)
    }
  }

  private setFocusedOption(option: DapDSOptionItem) {
    const renderedItem = this.getAllRenderedOptions().find(
      el => el.value === option.value,
    )

    if (renderedItem) {
      renderedItem.focus()
    }
  }

  private setSelectedOption(option?: DapDSOptionItem | undefined) {
    this.availableOptions.forEach(el => (el.selected = false))

    if (option) {
      this.currentOption = option

      option.selected = true
    }
  }

  private selectionChanged(option?: DapDSOptionItem | undefined, value?: any) {
    const oldValue = value || this.value

    if (option) {
      if (option.value !== oldValue) {
        this.value = option.value
      }
    }
  }

  private changeOption(option: DapDSOptionItem, value?: any) {
    if (option && !option.disabled) {
      this.selectedIndex = this.findIndexByValue(
        this.availableOptions,
        option.value,
      )

      this.setSelectedOption(option)
      this.setCurrentOption(option)
      this.selectionChanged(option, value)
    }
  }

  private readonly handleOptionClick = (event: MouseEvent) => {
    this.focused = true
    const option = event.target as DapDSOptionItem

    if (option && !option.disabled) {
      this.setCurrentOption(option)
      this.setSelectedOption(option)
      this.selectionChanged(option)

      this.updateComplete.then(() => {
        this.selectedIndex = this.findIndexByValue(
          this.availableOptions,
          this.value,
        )

        if (!this.disableDelaySet) {
          this.emit('dds-option-change', {
            ...this.getEventDetails(this.currentOption),
          })
        }
      })
    } else {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  getSearchString = (char: string) => {
    if (typeof this.queryTimeout === 'number') {
      window.clearTimeout(this.queryTimeout)
    }

    this.queryTimeout = window.setTimeout(() => {
      this.queryString = ''
    }, 500)

    this.queryString += char
    return this.queryString
  }

  buildQueryString = (char: string) => {
    const queryString = this.getSearchString(char)

    for (const option of this.availableOptions) {
      const label = option.optionText.toLowerCase()

      if (label.startsWith(queryString.toLowerCase())) {
        this.selectedIndex = this.findIndexByValue(
          this.availableOptions,
          option.value,
        )
        this.setCurrentOption(option)
        break
      }
    }
  }

  private handleInitialFocus(): void {
    if (!this.value && this.selectedIndex < 0) {
      this.selectedIndex = 0
    } else {
      const index = this.value
        ? this.findIndexByValue(this.availableOptions, this.value)
        : this.selectedIndex

      if (index !== -1 && !this.isAutoComplete) {
        this.selectedIndex = index
        this.setCurrentOption(this.availableOptions[this.selectedIndex])
        this.setSelectedOption(this.availableOptions[this.selectedIndex])
        if (this.value) {
          this.selectionChanged(this.availableOptions[this.selectedIndex])
        }
      }
    }

    if (!this.selectedIndex) {
      this.setCurrentOption(this.availableOptions[0])
      this.setSelectedOption(this.availableOptions[0])
    }
  }

  private handleKeyFocus(key: string): void {
    if (this.isAutoComplete || this.searchMode === 'none') {
      this.handleKeyCodes(key)
      this.setCurrentOption(this.availableOptions[this.selectedIndex], false)
      this.setSelectedOption(this.availableOptions[this.selectedIndex])
    }

    if (this.searchMode === 'typehead') {
      this.buildQueryString(key)
    }
  }

  public captureFocus(key?: string): void {
    this.focused = true
    this.focus()

    if (!key) {
      this.handleInitialFocus()
    } else {
      this.handleKeyFocus(key)
    }
  }

  public removeFocus(): void {
    this.focused = false
    this.clearItemFocusStates()
  }

  private handleSlotChange() {
    const allOptions = this.getAllOptions()
    this.setOptionsFromSlottedItems()

    if (
      this.value &&
      allOptions.length > 0 &&
      this.searchOnValueSet === 'true'
    ) {
      const option = allOptions.find(el => el.value === this.value)
      this.setSelectedOption(option)
      this.selectionChanged(option)

      if (this.currentOption) {
        this.selectedIndex = this.findIndexByValue(allOptions, this.value)
        this.emit('dds-option-change', {
          ...this.getEventDetails(this.currentOption),
        })
      }
    } else if (
      this.value &&
      allOptions.length > 0 &&
      !this.search &&
      !this.isAutoComplete
    ) {
      const firstOption = allOptions.find(option => !option.disabled)
      this.selectedIndex = 0
      this.setCurrentOption(firstOption, false)

      this.emit('dds-current-change', {
        ...this.getEventDetails(firstOption),
      })
    } else if (
      this.value &&
      allOptions.length > 0 &&
      (this.search || this.isAutoComplete)
    ) {
      const firstOption = allOptions.find(option => !option.disabled)

      if (
        !this.currentOption ||
        this.currentOption.value !== firstOption?.value
      ) {
        this.selectedIndex = 0
        this.setCurrentOption(firstOption, false)
      }
    }
  }

  render() {
    return html`<div
      part="base"
      class=${clsx('option-list', {
        'option-list--sm': this.size === 'sm',
        'option-list--lg': this.size === 'lg',
      })}
      @keydown=${this.handleKeydown}
      @focus=${this.captureFocus}>
      ${this.availableOptions.map(
        (option, index) => html`
          <dap-ds-option-item
            size=${this.size}
            ?disabled=${option.disabled}
            ?selected=${this.value === option.value}
            ?focused=${index === this.selectedIndex}
            label=${ifDefined(option.label)}
            role="option"
            value=${option.value}
            tabIndex=${option.tabIndex}
            id=${option.id || `dap-ds-option-${option.value}`}
            ?selectable=${this.selectable}
            @click=${this.handleOptionClick}>
            ${unsafeStatic(option.innerHTML)}
          </dap-ds-option-item>
        `,
      )}
      <slot hidden @slotchange=${this.handleSlotChange}></slot>
    </div> `
  }
}
